import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipoambiente.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const TipoClassAmbienteManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    classificacao: '',
    cor: '',
    descricao: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/tipo-class-ambiente');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
  }, [verificarAPI]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'classificação de ambiente', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/tipo-class-ambiente/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'classificação de ambiente', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'classificação de ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'classificação de ambiente', { id });
    try {
      const response = await api.delete(`/tipo-class-ambiente/${id}`);
      logarSucesso('Excluir', 'classificação de ambiente', response.data);
      const novosDados = dados.filter(item => item.id_class_amb !== id);
      setDados(novosDados);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'classificação de ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [dados, mostrarMensagem]);

  const colunas = useMemo(() => [
    {
      title: 'Salvar',
      field: 'salvar',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        atualizarRegistro(data.id_class_amb, data);
      },
      headerSort: false
    },
    {
      title: 'Excluir',
      field: 'excluir',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        if (window.confirm(`Deseja realmente excluir o registro "${data.id_class_amb}"?`)) {
          excluirRegistro(data.id_class_amb);
        }
      },
      headerSort: false
    },
    {
      title: 'ID',
      field: 'id_class_amb',
      width: 150,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'string'
    },
    {
      title: 'Classificação',
      field: 'classificacao',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar classificação...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: 'Cor',
      field: 'cor',
      width: 150,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar cor...',
      sorter: 'string',
      hozAlign: "center",
      editor: (cell, onRendered, success, cancel) => {
        const input = document.createElement("input");
        input.setAttribute("type", "color");
        input.value = cell.getValue() || "#000000";
        input.style.width = "100%";
        input.style.height = "100%";
        input.style.padding = "0px";
        input.style.border = "none";
        input.style.cursor = "pointer";

        onRendered(() => {
          input.focus();
          input.click();
        });

        const onChange = () => {
          success(input.value);
        };

        input.addEventListener("change", onChange);
        input.addEventListener("blur", onChange);

        return input;
      },
      formatter: function (cell, formatterParams, onRendered) {
        const color = cell.getValue();
        if (!color) return '';

        const span = document.createElement('span');
        span.style.backgroundColor = color;
        span.style.width = '24px';
        span.style.height = '24px';
        span.style.borderRadius = '4px';
        span.style.display = 'inline-block';
        span.style.border = '1px solid #ccc';
        span.setAttribute('title', color); // Show code on hover

        return span;
      },
    },
    {
      title: 'Descrição',
      field: 'descricao',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar descrição...',
      sorter: 'string',
      editor: 'input'
    }
  ], [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/tipo-class-ambiente');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_class_amb'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!formData.classificacao.trim()) {
      camposObrigatorios.push('Classificação');
      erros.push('classificacao');
    }
    if (!formData.cor.trim()) {
      camposObrigatorios.push('Cor');
      erros.push('cor');
    }
    if (!formData.descricao.trim()) {
      camposObrigatorios.push('Descrição');
      erros.push('descricao');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      classificacao: formData.classificacao,
      cor: formData.cor,
      descricao: formData.descricao
    };

    logarRequisicao('Adicionar', 'classificação de ambiente', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/tipo-class-ambiente', dataToSend);
      logarSucesso('Adicionar', 'classificação de ambiente', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ classificacao: '', cor: '', descricao: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'classificação de ambiente');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'tipos_class_ambiente.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Tipos de Classificação de Ambiente</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tipo_de_class_ambiente</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/tipos de classificacao ambiente.mov" buttonText="Ver Tutorial Classificação" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="classificacao">Classificação:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="classificacao"
              name="classificacao"
              value={formData.classificacao}
              onChange={handleInputChange}
              placeholder="Ex: Ensino Fundamental"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('classificacao') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cor">Cor:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="color"
              id="cor"
              name="cor"
              value={formData.cor || "#000000"}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('cor') ? 'input-error' : ''}
              style={{ padding: '2px', height: '38px' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="descricao">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="descricao"
              name="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              placeholder="Ex: Descrição da classificação"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('descricao') ? 'input-error' : ''}
            />
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default TipoClassAmbienteManager;